import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

// components
import Header from "./Components/Layout/Header";
import Auth from "./Components/Auth";
import Products from "./Components/Product/Products";
import ProductSearch from "./Components/Product/ProductSearch";
import ProductV2 from './Components/Product/V2/Product';

// Auth route

import AuthRoute from './Util/AuthRoute';
import AdminAuthRoute from './Util/AdminAuthRoute';
import CombinedAuthRoute from './Util/CombinedAuthRoute';
import RegistrationAuthRoute from './Util/RegistrationAuthRoute';

// styles
import './App.css';

// context
import { ProductContextProvider } from './Context/ProductContext';
import { AppContextProvider } from './Context/AppContextProvide';
import { ProductsViewedContextProvider } from './Context/ProductsViewedContextProvider';
import { AdminTaxContextProvider } from './Context/AdminTaxContextProvider';
import { AdminCompanyMarginContextProvider } from './Context/AdminCompanyMarginContextProvider';
import { BulletinContextProvider } from './Context/BulletinContextProvider';
import { AdminContextProvider } from './Context/AdminContextProvider';
import { AnnouncementsContextProvider } from './Context/AnnouncementsContextProvider';
import { AlertProvider } from './Context/AlertContextProvider';
import { UserAnnouncementsContextProvider } from './Context/UserAnnouncementsContextProvider';
import { ProductStatsContextProvider } from './Context/ProductsStatsContextProvider';
import { CompanyStatsContextProvider } from './Context/CompaniesStatsContextProvider';
import { UserBulletinContextProvider } from './Context/UserBulletinContextProvider';
import { PrintQueueContextProvider } from './Context/PrintQueueContextProvider';
import { AdminLoadedMarginContextProvider } from './Context/AdminLoadedMarginContextProvider';
import { AdminMaterialClassMarginContextProvider } from './Context/AdminMaterialClassMarginContextProvider';
import { ProductDisclaimerContextProvider } from './Context/ProductDisclaimerContextProvider';
import { TaxDisclaimerContextProvider } from './Context/TaxDisclaimerContextProvider';
import { ProductsWithSearchContextProvider } from './Context/ProductsWithSearchContextProvider';
import { CompanyLogoContextProvider } from './Context/CompanyLogoContextProvider';
import { LookBackContextProvider } from './Context/LookBackContextProvider';
import { HubSpokeContextProvider } from './Context/HubSpokeContextProvider';
import { CalculationVisualizerContextProvider } from './Context/CalculationVisualizerContextProvider';
import { TimezoneContextProvider, useZoneVersion } from './Context/TimezoneContextProvider';

import Notfound from "./Components/Notfound";
import Home from "./Components/LandingPage/Home";
import Admin from "./Components/Admin/Admin";
import Stats from "./Components/LandingPage/Stats/Stats";
import ScanDashboard from "./Components/LandingPage/Insights/ScanDashboard";
import User from "./Components/User/User";
import Signin from "./Components/LandingPage/Signin";
import ImportExport from "./Components/ImportExport/index";
import Settings from "./Components/Settings/Settings";
import FileHistory from "./Components/LandingPage/FileHistory/FileHistory";
import About from "./Components/About/About";
import Register from "./Components/User/Register";
import ConfirmUser from "./Components/User/ConfirmUser";
import ForgotPass from "./Components/LandingPage/ForgotPass";
import ResetPass from "./Components/LandingPage/ResetPass";
import CustomerHome from "./Components/Customer/CustomerHome";
import ManageSamples from "./Components/Samples/ManageSamples";
import { ProgressProvider } from "./Context/ProgressContextProvider";
import LeadManagement from "./Components/LeadManagement/LeadManagement";
import FeatureRequests from "./Components/FeatureRequests/FeatureRequests";
import { getFileProcessingSelection, getShowVegasMarketingSelection } from './Actions';
import Prompt from './Components/AI/Prompt';
import LabelScanner from './Components/AI/LabelScanner';
import HiddenAdminAuthRoute from './Util/HiddenAdminAuthRoute';
import Tools from './Components/InternalTools/Tools';
import HelpVideoReview from './Components/HelpVideoReview';
import { createTheme, ThemeProvider } from '@material-ui/core';
import VegasMarketing from './Components/Tradeshow/VegasMarketing';
import Root from './Components/Root';
import FlowView from './Components/Admin/CustomCalculations/FlowView';
import FileProcessing from './Components/FileProcessing';
import MappingBuilder from './Components/Admin/MappingBuilder';
import DuplicateDetection from './Components/Admin/DuplicateDetection/DuplicateDetection';
import WhatsNewModal from './Components/Shared/WhatsNew/WhatsNewModal';
import OnboardingModal from './Components/Shared/WhatsNew/OnboardingModal';
import TourImageDialog from './Components/Shared/WhatsNew/TourImageDialog';
import TourProductsPage from './Components/Tour/TourProductsPage';
import MaintenanceOverlay from './Components/Shared/MaintenanceOverlay';

const theme = createTheme();

// Re-mounts the routed page when the active timezone changes so timestamps
// formatted via the plain module helpers (not the context hook) re-read the new
// zone immediately, without the user having to navigate away and back.
function ZoneReactive({ children }) {
  const version = useZoneVersion();
  return <React.Fragment key={version}>{children}</React.Fragment>;
}

export default function App() {
  const [showVegasMarketingSwitchState, setShowVegasMarketingSwitchState] = useState(true);
  const [fileProcessingEnabled, setFileProcessingEnabled] = useState(false);

  useEffect(() => {
    async function getPageToggles () {
      // Independent toggles — fire concurrently instead of one-at-a-time.
      await Promise.all([
        getShowVegasMarketingSelection()
          .then((result) => setShowVegasMarketingSwitchState(Number(result.data) === 1))
          .catch(() => setShowVegasMarketingSwitchState(false)),
        getFileProcessingSelection()
          .then((result) => setFileProcessingEnabled(Number(result.data) === 1))
          .catch(() => setFileProcessingEnabled(false)),
      ]);
    }

    getPageToggles();
  }, [])

  return (
    <div className="App">
      <MaintenanceOverlay />
      <ThemeProvider theme={theme}>
        <AlertProvider>
          <ProgressProvider>
            <Router>
              <AppContextProvider>
                <TimezoneContextProvider>
                <CompanyLogoContextProvider>
                  <Header fileProcessingEnabled={fileProcessingEnabled} />
                </CompanyLogoContextProvider>
                <OnboardingModal />
                <WhatsNewModal />
                <TourImageDialog />
                <div className="app-content">
                  <ZoneReactive>
                  <Switch>
                    <Route exact path="/" render={() => <Root />}/>
                    <Route render={() => <Auth />} path="/auth" />
                    <Route
                      render={() => (
                        <CompanyLogoContextProvider>
                          <Signin />
                        </CompanyLogoContextProvider>
                      )}
                      path="/signin"
                    />
                    {/*  */}
                    <Route
                      render={() => (
                        <CompanyLogoContextProvider>
                          <RegistrationAuthRoute>
                            <Register />
                          </RegistrationAuthRoute>
                        </CompanyLogoContextProvider>
                      )}
                      path="/register"
                    />
                    {/*  */}
                    <Route
                      render={() => (
                        <CompanyLogoContextProvider>
                          <ConfirmUser />
                        </CompanyLogoContextProvider>       
                      )}
                      path="/confirm-user"
                    />
                    {/*  */}
                    <Route render={() => <ForgotPass />} path="/forgotpass" />
                    <Route render={() => <ResetPass />} path="/resetpass" />
                    <AuthRoute
                      path="/home"
                      exact
                      render={() => (
                        <HubSpokeContextProvider>
                          <LookBackContextProvider>
                            <CompanyStatsContextProvider>
                              <ProductStatsContextProvider>
                                <UserAnnouncementsContextProvider>
                                  <UserBulletinContextProvider>
                                    <Home />
                                  </UserBulletinContextProvider>
                                </UserAnnouncementsContextProvider>
                              </ProductStatsContextProvider>
                            </CompanyStatsContextProvider>
                          </LookBackContextProvider>
                        </HubSpokeContextProvider>
                      )}
                    />

                    <Route path="/user" exact render={() => <User />}></Route>

                    <AuthRoute
                      path="/lead-management"
                      exact
                      render={() => <LeadManagement />}
                    ></AuthRoute>

                    <AuthRoute
                      path="/feature-requests"
                      exact
                      render={() => <FeatureRequests />}
                    ></AuthRoute>

                    <AdminAuthRoute
                      path="/filehistory"
                      exact
                      render={() => <FileHistory />}
                    />

                    <AuthRoute
                      path="/stats"
                      exact
                      render={() => (
                        <HubSpokeContextProvider>
                          <ProductsViewedContextProvider>
                            <CompanyStatsContextProvider>
                              <ProductStatsContextProvider>
                                <Stats />
                              </ProductStatsContextProvider>
                            </CompanyStatsContextProvider>
                          </ProductsViewedContextProvider>
                        </HubSpokeContextProvider>
                      )}
                    />

                    <AuthRoute
                      path="/insights"
                      exact
                      render={() => (
                        <HubSpokeContextProvider>
                          <ScanDashboard />
                        </HubSpokeContextProvider>
                      )}
                    />

                    <AdminAuthRoute
                      path="/admin"
                      exact
                      render={() => {
                        return (
                          <BulletinContextProvider>
                            <AdminContextProvider>
                              <AnnouncementsContextProvider>
                                <AdminTaxContextProvider>
                                  <AdminCompanyMarginContextProvider>
                                    <AdminLoadedMarginContextProvider>
                                      <AdminMaterialClassMarginContextProvider>
                                          <ProductDisclaimerContextProvider>
                                            <TaxDisclaimerContextProvider>
                                              <Admin />
                                            </TaxDisclaimerContextProvider>
                                          </ProductDisclaimerContextProvider>
                                      </AdminMaterialClassMarginContextProvider>
                                    </AdminLoadedMarginContextProvider>
                                  </AdminCompanyMarginContextProvider>
                                </AdminTaxContextProvider>
                              </AnnouncementsContextProvider>
                            </AdminContextProvider>
                          </BulletinContextProvider>
                        );
                      }}
                    />

                    <AuthRoute
                      render={() => {
                        return (
                          <ProductsWithSearchContextProvider>
                            <PrintQueueContextProvider>
                              <ProductContextProvider>
                                <Products />
                              </ProductContextProvider>
                            </PrintQueueContextProvider>
                          </ProductsWithSearchContextProvider>
                        );
                      }}
                      path="/products"
                      exact
                    />

                    <AuthRoute
                      render={() => {
                        return (
                          <ProductsWithSearchContextProvider>
                            <PrintQueueContextProvider>
                              <ProductContextProvider>
                                <ProductSearch />
                              </ProductContextProvider>
                            </PrintQueueContextProvider>
                          </ProductsWithSearchContextProvider>
                        );
                      }}
                      path="/search"
                      exact
                    />

                    <AdminAuthRoute
                      path="/importexport"
                      exact
                      render={() => {
                        return (
                          <AdminContextProvider>
                            <HubSpokeContextProvider>
                              <ImportExport />
                            </HubSpokeContextProvider>
                          </AdminContextProvider>
                        );
                      }}
                    />

                    <HiddenAdminAuthRoute
                      path="/file-processing"
                      exact
                      featureEnabled={fileProcessingEnabled}
                      render={() => {
                        return (
                          <AdminContextProvider>
                            <FileProcessing />
                          </AdminContextProvider>
                        );
                      }}
                    />

                    <HiddenAdminAuthRoute
                      path="/mapping-builder"
                      exact
                      render={() => {
                        return (
                          <AdminContextProvider>
                            <MappingBuilder />
                          </AdminContextProvider>
                        );
                      }}
                    />

                    <AdminAuthRoute
                      path="/settings"
                      exact
                      render={() => {
                        return (
                          <AdminContextProvider>
                            <CompanyLogoContextProvider>
                              <Settings />
                            </CompanyLogoContextProvider> 
                          </AdminContextProvider>
                        );
                      }}
                    />

                    <HiddenAdminAuthRoute
                      path="/duplicate-detection"
                      exact
                      render={() => <DuplicateDetection />}
                    />

                    <HiddenAdminAuthRoute
                      path="/internal-tools"
                      exact
                      render={() => {
                        return (
                          <Tools />
                        );
                      }}
                    />
                    <HiddenAdminAuthRoute
                      path="/help-video-review"
                      exact
                      render={() => {
                        return (
                          <HelpVideoReview />
                        );
                      }}
                    />

                    <HiddenAdminAuthRoute
                      path="/calculations/flow"
                      exact
                      render={() => {
                        return (
                          <CalculationVisualizerContextProvider>
                            <FlowView />
                          </CalculationVisualizerContextProvider>
                        );
                      }}
                    />

                    <HiddenAdminAuthRoute
                      path="/ai/prompt"
                      exact
                      render={() => {
                        return (
                          <Prompt />
                        );
                      }}
                    />

                    <HiddenAdminAuthRoute
                      path="/ai/label-scan"
                      exact
                      render={() => <LabelScanner />}
                    />

                    <AuthRoute
                      path="/about"
                      exact
                      render={() => {
                        return (
                          <CompanyLogoContextProvider>
                            <About />
                          </CompanyLogoContextProvider>
                        );
                      }}
                    />

                    <CombinedAuthRoute
                      path="/customer-home"
                      exact
                      render={() => {
                        return <CustomerHome />;
                      }}
                    />

                    <AuthRoute
                      path="/manage-samples"
                      exact
                      render={() => {
                        return <ManageSamples />;
                      }}
                    />

                    {/* QR codes / printed labels encode the bare /product/:productId
                        path (see server getPrintFile), so V2 must own it unconditionally. */}
                    <Route
                      exact
                      path={[`/product/v2/:productId`, `/product/:productId`]}
                      render={() => (
                        <PrintQueueContextProvider>
                          <ProductContextProvider>
                            <HubSpokeContextProvider>
                              <ProductStatsContextProvider lazy>
                                <CalculationVisualizerContextProvider>
                                  <ProductV2 />
                                </CalculationVisualizerContextProvider>
                              </ProductStatsContextProvider>
                            </HubSpokeContextProvider>
                          </ProductContextProvider>
                        </PrintQueueContextProvider>
                      )}
                    />

                    <Route
                      exact
                      render={() => {
                        if (showVegasMarketingSwitchState) {
                          return <VegasMarketing />;
                        }

                        return <Notfound />;
                      }}
                      path={`/tradeshow/vegas`}
                    />
                    
                    <Route exact path="/tour/products" render={() => <TourProductsPage />} />

                    <Route render={() => <Notfound />} />
                  </Switch>
                  </ZoneReactive>
                </div>
                </TimezoneContextProvider>
              </AppContextProvider>
            </Router>
          </ProgressProvider>
        </AlertProvider>
      </ThemeProvider>
    </div>
  );
};
